import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  ComponentFactoryResolver,
  Inject,
  Injectable,
  Injector,
  Type,
  ViewContainerRef
} from '@angular/core';
import { DialogId } from '@bca-shared/models/_types';
import { IDialogConfig } from '@bca-core/modules/dialog/models/dialog-config';
import { DialogContainer } from '@bca-core/modules/dialog/models/dialog-container';
import { DialogRef } from '@bca-core/modules/dialog/models/dialog-ref';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, takeWhile } from 'rxjs/operators';

@Injectable()
export class DialogService {
  constructor(
    private _applicationRef: ApplicationRef,
    private _injector: Injector,
    @Inject(DOCUMENT) private _document: HTMLDocument
  ) {}
  private _openDialogs = new BehaviorSubject<DialogRef<any>[]>([]);

  open<T, P>(
    component: Type<T>,
    identifier: DialogId,
    viewContainerRef: ViewContainerRef,
    config?: IDialogConfig,
    props?: any,
    dispatch?: (visible: boolean, identifier: DialogId) => void,
  ): DialogRef<T> {
    if (this.getDialogById(identifier)) {
      throw Error(
        `Dialog with identifier "${identifier}" exists already. The dialog identifier must be unique.`
      );
    }

    const container = new DialogContainer<T, P>(
      viewContainerRef,
      this._applicationRef,
      this._injector,
      this._document
    );

    const dialogRef = container.create(
      component,
      identifier,
      {
        disableBackdrop: false,
        disableClose: false,
        disableAnimation: false,
        scrollableBackground: false,
        backdropShouldClose: false,
        escapeKeyShouldClose: true,
        ...config
      },
      props
    );

    dialogRef
      .beforeClosed()
      .pipe(takeWhile(() => dialogRef.instance != null))
      .subscribe(() => {
        const openDialogs = this._openDialogs.getValue();
        this._openDialogs.next(
          openDialogs.filter(dialog => dialog.identifier !== dialogRef.identifier)
        );
        if (dispatch) {
          dispatch(false, dialogRef.identifier);
        }
      });
    this._openDialogs.next([...this._openDialogs.getValue(), dialogRef]);

    if (dispatch) {
      dispatch(true, dialogRef.identifier);
    }

    return dialogRef;
  }

  close(identifier: DialogId): void {
    const openDialogs = this._openDialogs.getValue();

    const dialogRef = openDialogs.find(dialog => dialog.identifier === identifier);
    if (dialogRef) {
      dialogRef.close();
    }
  }

  closeAll(): void {
    const openDialogs = this._openDialogs.getValue();
    openDialogs.forEach(dialogRef => dialogRef.close());
  }

  getAll(): DialogRef<any>[] {
    return this._openDialogs.getValue();
  }

  getAll$(): Observable<DialogRef<any>[]> {
    return this._openDialogs.asObservable();
  }

  getDialogById$<T>(identifier: DialogId): Observable<DialogRef<T>> {
    return this._openDialogs.asObservable().pipe(
      map(dialogs => {
        return dialogs.find(dialogRef => dialogRef.identifier === identifier);
      })
    );
  }

  getDialogById<T>(identifier: DialogId): DialogRef<T> {
    const openDialogs = this._openDialogs.getValue();
    return openDialogs.find(dialog => dialog.identifier === identifier);
  }
  
  getOpenDialog(): any
 {
  return this._openDialogs
 }}
